'use client';

import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';

import Select from '@/components/Select/Select';

import { Session } from '@/lib/getSessionPages';
import { SORT_OPTIONS } from '@/modules/league/new-components/constants';
import {
  Heading,
  MaxPlayersInfo,
} from '@/modules/league/new-components/rsvp-components/PlayersBoxComponent/Heading';
import {
  CheckInSection,
  PlayerList,
} from '@/modules/league/new-components/rsvp-components/PlayersBoxComponent/PlayersList';
import { GameDayInfo, Player, SortOption } from '@/modules/league/new-components/types';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';

interface PlayingCardProps {
  matchDateId?: string;
  isLockOptInOut?: boolean;
  isMatchStarted?: boolean;
  players: Player[];
  isFlexLeague?: boolean;
  isLockScores?: Date;
  playerTypeTitle?: string;
  numberOfFutureDates?: number;
  isOptInPassed?: boolean;
  headerContent: GameDayInfo;
  cardName: string;
  user?: Session;
  playingCounter: number;
  maxPlayingCount: number;
  isClubMember?: boolean;
  isAuthorized: boolean;
  leagueId: string;
  slug: string;
  sessionId: string;
}

const PlayersBoxComponent = ({
  players,
  headerContent,
  cardName,
  user,
  playingCounter,
  maxPlayingCount,
  isClubMember,
  isAuthorized,
  leagueId,
  slug,
  sessionId,
}: PlayingCardProps) => {
  const { details } = headerContent;

  const chargeOptIn = details.chargeOptIn
    ? isClubMember
      ? parseFloat(details.costMember) > 0
      : parseFloat(details.costNonMember) > 0
    : false;

  const searchParams = useSearchParams();
  const [sortedPlayingPlayers, setSortedPlayingPlayers] = useState<Player[]>(players);
  const [sortedNotPlayingPlayers, setSortedNotPlayingPlayers] = useState<Player[]>(players);
  const [sortedWaitlistPlayers, setSortedWaitlistPlayers] = useState<Player[]>(players);
  const [sortPlayingPlayersState, setSortPlayingPlayersState] = useState<SortOption>({
    label: 'Rank',
    value: 'ranks',
  });
  const [sortNotPlayingPlayersState, setSortNotPlayingPlayersState] = useState<SortOption>({
    label: 'Rank',
    value: 'ranks',
  });
  const [sortWaitlistPlayersState, setSortWaitlistPlayersState] = useState<SortOption>({
    label: 'Datetime',
    value: 'date',
  });
  const [playersForOptIn, setPlayersForOptIn] = useState<Player[]>([]);
  const [playersForOptOut, setPlayersForOptOut] = useState<Player[]>([]);
  const [playersForRemove, setPlayersForRemove] = useState<Player[]>([]);
  const [playersForWait, setPlayersForWait] = useState<Player[]>([]);
  const [playersForCheckIn, setPlayersForCheckIn] = useState(
    players.filter((player) => player.UserCheckIn_1).length
  );
  const [uncheckedPlayers, setUncheckedPlayers] = useState(
    players.filter((player) => !player.UserCheckIn_1)
  );
  const [showOnlyUncheckedPlayers, setShowOnlyUncheckedPlayers] = useState(false);

  const [searchTermPlaying, setSearchTermPlaying] = useState<string>('');

  const [location, setLocation] = useState('');

  const { hasPermission } = useAuth();

  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

  const canShowPlayingDate = isSuperAdmin || headerContent?.details?.showPlayingDate;
  const canShowWaitlistDate = isSuperAdmin || headerContent?.details?.showWaitlistDate;

  useEffect(() => {
    setPlayersForCheckIn(players.filter((player) => player.UserCheckIn_1).length);
  }, [players]);

  useEffect(() => {
    setUncheckedPlayers(players.filter((player) => !player.UserCheckIn_1));
  }, [players]);

  useEffect(() => {
    // Set default URL params if not present
    if (!searchParams?.get('sortByP')) {
      newSearchParams.set('sortByP', 'rank');
    }
    if (!searchParams?.get('sortByNP')) {
      newSearchParams.set('sortByNP', 'rank');
    }
    if (!searchParams?.get('sortByW')) {
      newSearchParams.set('sortByW', canShowWaitlistDate ? 'date' : 'rank');
    }

    // Set players list params for the initial load
    newSearchParams.set('playersListP', 'Playing');
    newSearchParams.set('playersListNP', 'Not Playing');
    newSearchParams.set('playersListW', 'Waitlist');

    // Replace the URL with the updated searchParams (but don't reload the page)
    if (router) {
      router.replace(`?${newSearchParams?.toString()}`, { scroll: false });
    }
  }, []);

  useEffect(() => {
    const sortPBy = searchParams?.get('sortByP') || 'rank';
    const sortNPBy = searchParams?.get('sortByNP') || 'rank';
    const sortWBy = searchParams?.get('sortByW') || 'date';

    // Check the card name and sort only the relevant list
    if (cardName === 'Playing') {
      const sorted = [...players].sort((a, b) => {
        if (sortPBy === 'asc') {
          return (a.PlayerFullName_1 || '').localeCompare(b.PlayerFullName_1 || '');
        } else if (sortPBy === 'desc') {
          return (b.PlayerFullName_1 || '').localeCompare(a.PlayerFullName_1 || '');
        } else if (sortPBy === 'date') {
          return new Date(a.DateOptIn || '').getTime() - new Date(b.DateOptIn || '').getTime();
        } else {
          return (
            parseInt(a.StatsSeedOnGameDay || '0', 10) - parseInt(b.StatsSeedOnGameDay || '0', 10)
          );
        }
      });
      setSortedPlayingPlayers(sorted);
    } else if (cardName === 'Not Playing') {
      const sorted = [...players].sort((a, b) => {
        if (sortNPBy === 'asc') {
          return (a.PlayerFullName_1 || '').localeCompare(b.PlayerFullName_1 || '');
        } else if (sortNPBy === 'desc') {
          return (b.PlayerFullName_1 || '').localeCompare(a.PlayerFullName_1 || '');
        } else {
          return (
            parseInt(a.StatsSeedOnGameDay || '0', 10) - parseInt(b.StatsSeedOnGameDay || '0', 10)
          );
        }
      });
      setSortedNotPlayingPlayers(sorted);
    } else if (cardName === 'Waitlist') {
      const sorted = [...players].sort((a, b) => {
        if (sortWBy === 'date') {
          return new Date(a.DateCreated || '').getTime() - new Date(b.DateCreated || '').getTime();
        } else if (sortWBy === 'asc') {
          return (a.PlayerFullName_1 || '').localeCompare(b.PlayerFullName_1 || '');
        } else if (sortWBy === 'desc') {
          return (b.PlayerFullName_1 || '').localeCompare(a.PlayerFullName_1 || '');
        } else {
          return (
            parseInt(a.StatsSeedOnGameDay || '0', 10) - parseInt(b.StatsSeedOnGameDay || '0', 10)
          );
        }
      });
      setSortedWaitlistPlayers(sorted);
    }
  }, [players, cardName, searchParams]);

  useEffect(() => {
    setLocation(window.location.href);
  }, []);

  const router = useRouter();
  const newSearchParams = new URLSearchParams(searchParams?.toString());
  const toggleOptIn = async (player: Player) => {
    setPlayersForOptIn((prevPlayersForOptIn) => {
      if (
        (prevPlayersForOptIn.length < maxPlayingCount - playingCounter || maxPlayingCount === -1) &&
        !prevPlayersForOptIn.some((arrPlayer) => arrPlayer.UserID_1 === player.UserID_1)
      ) {
        return [...prevPlayersForOptIn, player]; // Add player
      } else {
        return prevPlayersForOptIn.filter((arrPlayer) => arrPlayer.UserID_1 !== player.UserID_1); // Remove player
      }
    });
  };
  const toggleOptOut = async (player: Player) => {
    setPlayersForOptOut((prevPlayersForOptOut) => {
      if (!prevPlayersForOptOut.some((arrPlayer) => arrPlayer.UserID_1 === player.UserID_1)) {
        return [...prevPlayersForOptOut, player]; // Add player
      } else {
        return prevPlayersForOptOut.filter((arrPlayer) => arrPlayer.UserID_1 !== player.UserID_1); // Remove player
      }
    });
  };
  const toggleRemove = async (player: Player) => {
    setPlayersForRemove((prevPlayersForRemove) => {
      if (!prevPlayersForRemove.some((arrPlayer) => arrPlayer.UserID_1 === player.UserID_1)) {
        return [...prevPlayersForRemove, player]; // Add player
      } else {
        return prevPlayersForRemove.filter((arrPlayer) => arrPlayer.UserID_1 !== player.UserID_1); // Remove player
      }
    });
  };
  const toggleWait = async (player: Player) => {
    setPlayersForWait((prevPlayersForWait) => {
      if (!prevPlayersForWait.some((arrPlayer) => arrPlayer.UserID_1 === player.UserID_1)) {
        return [...prevPlayersForWait, player]; // Add player
      } else {
        return prevPlayersForWait.filter((arrPlayer) => arrPlayer.UserID_1 !== player.UserID_1); // Remove player
      }
    });
  };

  const filteredPlayingPlayers = useMemo(
    () =>
      players.filter(
        (player) =>
          player?.PlayerFullName_1?.toLowerCase().includes(searchTermPlaying.toLowerCase()) ||
          player?.PlayerFullName_2?.toLowerCase().includes(searchTermPlaying.toLowerCase())
      ),
    [players, searchTermPlaying]
  );

  const getSortOption = (cardName: string) => {
    if (cardName.trim().toLowerCase() === 'playing' && canShowPlayingDate) {
      return [{ label: 'Datetime', value: 'date' }, ...SORT_OPTIONS];
    }

    if (cardName.trim().toLowerCase() === 'waitlist' && canShowWaitlistDate) {
      return [{ label: 'Datetime', value: 'date' }, ...SORT_OPTIONS];
    }

    return SORT_OPTIONS;
  };

  return (
    <div className='flex h-auto flex-col gap-2 rounded-md bg-white shadow-md'>
      <Heading
        cardName={cardName}
        players={players}
        isAuthorized={
          isAuthorized && !details.isMatchStarted && details.NumberOfFutureMatchDatesStarted === 0
        }
        isOptInButtonDisabled={
          cardName === 'Not Playing' &&
          ((maxPlayingCount !== -1 && playersForOptIn.length > maxPlayingCount) ||
            playingCounter >= maxPlayingCount)
        }
        playersForOptOut={playersForOptOut.length}
        playersForOptIn={playersForOptIn.length}
        playersForRemove={playersForRemove.length}
        playersForWait={playersForWait.length}
        notPlayingPlayers={playersForOptIn}
        matchDateId={details.MatchDateID}
        setPlayersForOptIn={setPlayersForOptIn}
        setPlayersForOptOut={setPlayersForOptOut}
        setPlayersForRemove={setPlayersForRemove}
        playingPlayers={playersForOptOut}
        waitlistPlayers={playersForRemove}
        canMoveToWait={details?.isWaitlist && details?.maxPlayers !== -1}
        canOptIn={maxPlayingCount === -1 || playingCounter < maxPlayingCount}
        setPlayersForWait={setPlayersForWait}
        playersFromNotToWait={playersForWait}
        isLocked={details.isLocked}
        isPartnerLeague={
          headerContent.details.playerTypeTitle === 'Partner' ||
          players.some((player) => player.PlayerFullName_2 !== '' && player.UserID_2 !== null)
        }
        chargeOptIn={chargeOptIn}
        chargeOptInURL={`redirect_r.aspx?mf=l&todo=r&mid=${details.leagueId}&sid=${details.sessionId}&mdid=${details.MatchDateID}&cid=${details.clubId}&rulr=${encodeURIComponent(location)}`}
        leagueId={leagueId}
        slug={slug}
        sessionId={sessionId}
        userUuid={user?.pbUuid}
      />
      {(cardName === 'Playing' || cardName === 'Waitlist') && (
        <MaxPlayersInfo cardName={cardName} gameDayInfo={details} />
      )}
      {players.length > 0 && (
        <div className='flex flex-col gap-0.5 pl-4 pr-3'>
          <div className='shadow flex h-9 w-full items-center rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 focus-within:border-leagues-300/30 focus-within:ring focus-within:ring-leagues-300/20 hover:border-leagues-300/50 hover:ring hover:ring-leagues-300/10'>
            <DebounceInput
              minLength={1}
              debounceTimeout={300}
              className='flex-1 bg-transparent outline-none'
              placeholder='Search for a player...'
              value={searchTermPlaying}
              onChange={(e) => setSearchTermPlaying(e.target.value)}
            />
            {searchTermPlaying !== '' && (
              <FontAwesomeIcon
                icon={faCircleXmark}
                size='lg'
                className='ml-2 cursor-pointer text-error-500'
                onClick={() => setSearchTermPlaying('')}
              />
            )}
          </div>
        </div>
      )}
      {players.length > 0 && (
        <div className='flex flex-col gap-0.5 px-4'>
          <p className='text-xs font-medium text-gray-700'>Sort</p>
          <Select
            options={getSortOption(cardName)}
            extractKey={(item) => item.value.toString()}
            renderLabel={(item) => item.label}
            renderValue={(item) => item.label}
            width='100%'
            onChange={(option) => {
              if (cardName === 'Playing') {
                setSortPlayingPlayersState(option);
                newSearchParams.set('sortByP', option.value);
                newSearchParams.set('playersListP', cardName);
              } else if (cardName === 'Not Playing') {
                setSortNotPlayingPlayersState(option);
                newSearchParams.set('sortByNP', option.value);
                newSearchParams.set('playersListNP', cardName);
              } else if (cardName === 'Waitlist') {
                setSortWaitlistPlayersState(option);
                newSearchParams.set('sortByW', option.value);
                newSearchParams.set('playersListW', cardName);
              }

              router.replace(`?${newSearchParams.toString()}`, { scroll: false });
            }}
            value={
              cardName === 'Playing'
                ? sortPlayingPlayersState
                : cardName === 'Not Playing'
                  ? sortNotPlayingPlayersState
                  : sortWaitlistPlayersState
            }
            selectedBorderColor='border-leagues-300/50 ring ring-leagues-300/10'
            selectedBorderColorHover='hover:border-leagues-300/50 hover:ring hover:ring-leagues-300/10'
          />
        </div>
      )}
      <CheckInSection
        cardName={cardName}
        isAuthorized={isAuthorized}
        playingCounter={playingCounter}
        totalPlayersInCard={
          cardName === 'Playing'
            ? sortedPlayingPlayers.length
            : cardName === 'Not Playing'
              ? sortedNotPlayingPlayers.length
              : sortedWaitlistPlayers.length
        }
        toggleOptIn={toggleOptIn}
        toggleOptOut={toggleOptOut}
        toggleRemove={toggleRemove}
        toggleWait={toggleWait}
        players={
          cardName === 'Playing'
            ? sortedPlayingPlayers
            : cardName === 'Not Playing'
              ? sortedNotPlayingPlayers
              : sortedWaitlistPlayers
        }
        maxPlayingCount={maxPlayingCount}
        playersForCheckIn={playersForCheckIn}
        isLocked={details.isLocked}
        isPartnerLeague={
          headerContent.details.playerTypeTitle === 'Partner' ||
          players.some((player) => player.PlayerFullName_2 !== '' && player.UserID_2 !== null)
        }
        showOnlyUncheckedPlayers={showOnlyUncheckedPlayers}
        setShowOnlyUncheckedPlayers={setShowOnlyUncheckedPlayers}
      />
      <PlayerList
        userUuid={user?.pbUuid}
        players={
          cardName === 'Playing'
            ? showOnlyUncheckedPlayers
              ? uncheckedPlayers
              : searchTermPlaying !== ''
                ? filteredPlayingPlayers
                : sortedPlayingPlayers
            : cardName === 'Not Playing'
              ? searchTermPlaying !== ''
                ? filteredPlayingPlayers
                : sortedNotPlayingPlayers
              : searchTermPlaying !== ''
                ? filteredPlayingPlayers
                : sortedWaitlistPlayers
        }
        headerContent={headerContent}
        isAuthorized={isAuthorized}
        cardName={cardName}
        playersForOptIn={playersForOptIn}
        playersForOptOut={playersForOptOut}
        playersForRemove={playersForRemove}
        toggleOptIn={toggleOptIn}
        toggleOptOut={toggleOptOut}
        toggleRemove={toggleRemove}
        toggleWait={toggleWait}
        canMoveToWait={details?.isWaitlist && details?.maxPlayers !== -1}
        canOptIn={maxPlayingCount === -1 || playingCounter < maxPlayingCount}
        isOptInButtonDisabled={
          cardName === 'Not Playing' &&
          (maxPlayingCount === -1
            ? false
            : playersForOptIn.length > maxPlayingCount || playingCounter >= maxPlayingCount)
          // ((maxPlayingCount !== -1 && playersForOptIn.length > maxPlayingCount) || playingCounter >= maxPlayingCount)
        }
        playersForWait={playersForWait}
        playersForCheckIn={playersForCheckIn}
        setPlayersForCheckIn={setPlayersForCheckIn}
        setUncheckedPlayers={setUncheckedPlayers}
        uncheckedPlayers={uncheckedPlayers}
        chargeOptIn={chargeOptIn}
        chargeOptInURL={`redirect_r.aspx?mf=l&todo=r&mid=${details.leagueId}&sid=${details.sessionId}&mdid=${details.MatchDateID}&cid=${details.clubId}&rulr=${encodeURIComponent(location)}`}
        slug={slug}
      />
    </div>
  );
};

export default PlayersBoxComponent;
